import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminViewProgrammePage() {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [programme, setProgramme] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [competitionId]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load competition details
      const compResp = await axios.get(`${API}/competitions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const comp = compResp.data.find(c => c.id === competitionId);
      setCompetition(comp);
      
      // Load programme for Super Admin (includes withdrawn gymnasts)
      const progResp = await axios.get(`${API}/superadmin/programme/${competitionId}/view`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgramme(progResp.data);
    } catch (error) {
      console.error('Failed to load programme:', error);
      toast.error('Failed to load programme');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600">Loading programme...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/scorz-superadmin')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Programme (Super Admin View)
              </h1>
              <p className="text-gray-600 mt-1">{competition?.title}</p>
              <p className="text-sm text-gray-500 mt-2">
                Withdrawn gymnasts are highlighted in red
              </p>
            </div>
          </div>
        </div>

        {/* Programme Content */}
        {!programme || programme.rounds.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-500">
                No programme available for this competition yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {programme.rounds.map((round, roundIndex) => (
              <Card key={roundIndex}>
                <CardHeader>
                  <CardTitle>{round.roundName}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {round.groups.map((group, groupIndex) => (
                      <div key={groupIndex} className="border rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-3">{group.groupName}</h3>
                        <div className="space-y-2">
                          {group.gymnasts.map((gymData, gymIndex) => (
                            <div
                              key={gymIndex}
                              className={`flex items-center justify-between p-3 rounded ${
                                gymData.isWithdrawn 
                                  ? 'bg-gray-100 border-2 border-gray-400' 
                                  : 'bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center space-x-4 flex-1">
                                <div 
                                  className={`font-bold rounded px-3 py-1 text-lg ${
                                    gymData.isWithdrawn 
                                      ? 'bg-gray-800 text-white' 
                                      : 'bg-[#000000] text-white'
                                  }`}
                                >
                                  {gymData.number}{gymData.letter}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-medium ${gymData.isWithdrawn ? 'text-black' : ''}`}>
                                    {gymData.gymnastName || `Gymnast #${gymIndex + 1}`}
                                  </p>
                                  <p className={`text-xs ${gymData.isWithdrawn ? 'text-gray-900' : 'text-gray-600'}`}>
                                    {gymData.clubName || 'Unknown Club'} • {gymData.category || 'N/A'}
                                  </p>
                                </div>
                                {gymData.isWithdrawn && (
                                  <div className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded">
                                    WITHDRAWN
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {group.gymnasts.length === 0 && (
                            <p className="text-sm text-gray-500 text-center py-2">
                              No gymnasts assigned to this group yet.
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
